<template>
  <div class="profile-menu">
    <button
      v-click-outside="hide"
      type="button"
      @click.prevent="toggle"
    >
      <Avatar
        :src="currentUser ? currentUser.pictureUrl : ''"
        :gap="currentUser ? currentUser.getInitials() : ''"
        size="lg"
      />
    </button>
    <ul
      v-if="opened && currentUser"
      class="profile-menu__list"
    >
      <template v-for="(item, index) in menu">
        <div
          v-if="index === menu.length - 1"
          :key="`${index}-divider`"
          class="profile-menu__divider"
        ></div>

        <li
          v-if="item.visible"
          :key="index"
          class="profile-menu__item"
        >
          <button
            v-if="item.action"
            class="profile-menu__button"
            :class="{ 'is-active': isMenuItemActive(item) }"
            type="button"
            @click.prevent="onClick(item.action)"
          >
            <icon
              :data="item.icon"
              :fill="item.fill"
              class="profile-menu__icon"
              :color="item.iconColor"
              width="var(--icon-24)"
              height="var(--icon-24)"
            />
            {{ item.label }}
          </button>
          <router-link
            v-else
            class="profile-menu__button"
            :class="{ 'is-active': isMenuItemActive(item) }"
            :to="item.to"
            @click="hide"
          >
            <icon
              :data="item.icon"
              :fill="item.fill"
              class="profile-menu__icon"
              :class="`profile-menu__icon-${item.id}`"
              :color="item.iconColor"
              width="var(--icon-24)"
              height="var(--icon-24)"
            />
            {{ item.label }}
          </router-link>
        </li>
      </template>
    </ul>
    <ModalTheme />
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router"
import { useAcl } from "vue-simple-acl"
import accounts from "@icon/accounts.svg"
import aiConfig from "@icon/ai-config.svg"
import gear from "@icon/gear.svg"
import logout from "@icon/logout.svg"
import activityLog from "@icon/switch.svg"
import theme from "@icon/theme.svg"
import user from "@icon/user.svg"
import { directive as vClickOutside } from "click-outside-vue3"
import { computed, ref } from "vue"
import { useStore } from "vuex"

import Avatar from "@/components/Avatar/index.vue"

import { logout as keycloakLogout } from "@/plugins/keycloak"
import { useModal } from "@/plugins/vue-js-modal/index"
import { Actions, Getters } from "@/store"
import { normalizeParam } from "@/utils"

import ModalTheme from "../Modal/ModalTheme.vue"

import type { Account, User } from "@/models"
import type { RouteLocationRaw } from "vue-router"

interface IMenu {
  id: string
  icon: unknown
  iconColor?: string
  label?: string
  to?: RouteLocationRaw
  fill?: boolean
  visible?: boolean
  action?: "aiConfig" | "logOut" | "theme"
}

const store = useStore()
const route = useRoute()
const router = useRouter()
const modal = useModal()
const acl = useAcl()

const opened = ref(false)
const currentUser = computed<User>(() => store.getters[Getters.GET_USER_CURRENT_PROFILE])
const accountId = computed<string>(() => store.getters[Getters.GET_CURRENT_ACCOUNT_ID])
const sessionAccount = computed<Account | null>(() => store.getters[Getters.GET_SESSION_ACCOUNT])
const hasAiConfig = computed<boolean>(() => store.getters[Getters.HAS_AI_CONFIG])
const isSuperAdmin = computed(() => acl.can("superAdmin"))
const isAdmin = computed(() => acl.can.any(["admin", "superAdmin"]))
const menu = computed<IMenu[]>(() => {
  if (!currentUser.value) return []

  const routeAccountId = normalizeParam(route.params.accountId)
  const aiActivityLogRoute = isSuperAdmin.value
    ? { name: "ai-activity-log" }
    : {
        name: "ai-activity-log",
        params: {
          accountId: routeAccountId
        }
      }
  const routeToAccount = isSuperAdmin.value
    ? {
        name: "accounts"
      }
    : {
        name: "edit-account",
        params: {
          accountId: currentUser.value.accountName
        }
      }

  return [
    {
      id: "profile",
      icon: user,
      iconColor: "#2A2B36",
      label: "Profile",
      fill: false,
      visible: !!routeAccountId,
      to: {
        name: "edit-user",
        params: {
          accountId: routeAccountId,
          userId: currentUser.value.id
        }
      }
    },
    {
      id: "accounts",
      icon: accounts,
      iconColor: "#2A2B36",
      label: isSuperAdmin.value ? "Accounts" : "My Account",
      to: routeToAccount,
      fill: true,
      visible: isAdmin.value
    },
    {
      id: "settings",
      icon: gear,
      iconColor: "#2A2B36",
      label: "Settings",
      fill: false,
      visible: isSuperAdmin.value,
      to: {
        name: "settings"
      }
    },
    {
      id: "aiConfig",
      icon: aiConfig,
      iconColor: "#2A2B36",
      label: "AI Config",
      fill: true,
      visible: isAdmin.value && acl.can("accessToAi"),
      action: "aiConfig"
    },
    {
      id: "aiActivityLog",
      icon: activityLog,
      label: "AI Activity Log",
      fill: true,
      visible:
        (isAdmin.value && acl.can("accessToAi")) ||
        isSuperAdmin.value,
      to: aiActivityLogRoute
    },
    {
      id: "theme",
      icon: theme,
      iconColor: "#2A2B36",
      label: "Theme",
      fill: true,
      visible: true,
      action: "theme"
    },
    {
      id: "logout",
      icon: logout,
      iconColor: "#2A2B36",
      label: "Log out",
      fill: false,
      visible: true,
      action: "logOut"
    }
  ]
})

function toggle() {
  opened.value = !opened.value
}

function hide() {
  opened.value = false
}

function isMenuItemActive(item: IMenu) {
  if (item.id !== "aiActivityLog") return false
  return route.name === "ai-activity-log"
}

function onClick(action: IMenu["action"]) {
  switch (action) {
    case "logOut":
      logoutUser()
      break
    case "theme":
      openThemeModal()
      break
    case "aiConfig":
      openAiConfigModal()
      break
    default:
      break
  }
}

async function openAiConfigModal() {
  hide()

  const currentAccountId =
    accountId.value || sessionAccount.value?.id || currentUser.value.accountId
  const currentAccountName = sessionAccount.value?.urlName || currentUser.value.accountName

  if (currentAccountId) {
    await store.dispatch(Actions.HAS_AI_CONFIG, currentAccountId)
  }

  if (!hasAiConfig.value) {
    modal.show("modal-ai-setup")
    return
  }

  router.push({
    name: "edit-account",
    params: {
      accountId: currentAccountName
    },
    query: {
      tab: "aiConfig"
    }
  })
}

function openThemeModal() {
  modal.show("modal-theme")
}

function logoutUser() {
  keycloakLogout()
}
</script>
